import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import type {
  CommitSummary,
  ImageImportMode,
  ImageImportSummary,
  ImageStats,
  ImportBatch,
  ImportParse,
  RevertSummary,
} from '../lib/types';
import { Banner, Empty, ErrorBox, Field, Loading, Table, Toolbar } from '../ui';

/** What the API returns instead of a result when it refuses. */
interface ApiError {
  error?: string;
}

/** Staged products committed per request — see the slicing note on `runCommit`. */
const SLICE = 500;

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <span>
      <b>{value}</b> <span className="text-muted">{label}</span>
    </span>
  );
}

function Preview({ p }: { p: ImportParse }) {
  return (
    <div className="mt-3">
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
        <span className="text-muted">{p.dryRun ? 'Preview — nothing written' : 'Staged'}</span>
        <Stat label="products" value={p.products} />
        <Stat label="OEs" value={p.oes} />
        <Stat label="cross" value={p.cross} />
        {p.vehicles > 0 && <Stat label="vehicle apps" value={p.vehicles} />}
        <Stat label="source rows" value={p.rawRows} />
      </div>

      {p.errors.length > 0 && (
        <div className="mono mb-2 max-h-32 overflow-auto text-[11.5px] text-bad">
          {p.errors.map((e, i) => (
            <div key={i}>
              row {e.row}: {e.message}
            </div>
          ))}
        </div>
      )}

      <Table cols={['Brand', 'Article', 'Name', 'Description', 'OEs', 'Cross', 'Attrs']}>
        {p.sample.map((s, i) => (
          <tr key={i}>
            <td className="mono">{s.brand}</td>
            <td className="mono">{s.article}</td>
            <td>{s.name ?? <span className="text-faint">—</span>}</td>
            <td className="text-[11.5px] text-muted">{s.description ?? <span className="text-faint">—</span>}</td>
            <td className="mono text-[11.5px]">{s.oes.join(', ') || <span className="text-faint">—</span>}</td>
            <td className="mono text-[11.5px]">{s.cross.join(', ') || <span className="text-faint">—</span>}</td>
            <td className="mono text-[11.5px] text-muted">
              {Object.keys(s.attrs).length ? Object.entries(s.attrs).map(([k, v]) => `${k}=${v}`).join('  ') : '—'}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/**
 * Bulk-load a supplier's photo folder from the sFTP drop.
 *
 * Two matching modes, because suppliers ship images two different ways. A file
 * may be named after the part it shows — or, as with SISTEMCAR, be an exploded
 * view of a whole assembly that belongs to a dozen parts at once, with the
 * `File_Grafico_*.xlsx` workbook beside it stating which part sits at which
 * callout. Dry run first: it reports what would attach without writing anything,
 * and a low match count almost always means the wrong mode or brand rather than
 * an empty folder.
 */
function ImageImport() {
  const { request } = useApi();
  const [dir, setDir] = useState('SISTEMCAR/IMG');
  const [brand, setBrand] = useState('SISTEMCAR');
  const [mode, setMode] = useState<ImageImportMode>('sistemcar');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [res, setRes] = useState<ImageImportSummary | null>(null);
  const [stats, setStats] = useState<ImageStats | null>(null);

  const loadStats = useCallback(async () => {
    const r = await request<ImageStats>('GET', '/v1/admin/images/stats');
    if (r.ok) setStats(r.data);
  }, [request]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  async function run(dryRun: boolean) {
    setErr('');
    setRes(null);
    setBusy(dryRun ? 'Scanning…' : 'Importing…');
    const r = await request<ImageImportSummary | ApiError>('POST', '/v1/admin/images/import', {
      dir,
      brand,
      mode,
      dryRun,
    });
    setBusy('');
    const d = r.data;
    if (d && 'files' in d) {
      setRes(d);
      if (!dryRun) void loadStats();
    } else setErr(d?.error ?? `Image import failed (HTTP ${r.status}).`);
  }

  return (
    <div className="mt-3.5">
      <div className="panel p-3.5">
        <div className="mb-2 flex items-baseline gap-2">
          <h2 className="text-[13px] font-semibold">Catalogue images</h2>
          <span className="text-[11.5px] text-faint">
            {stats
              ? `${stats.images.toLocaleString()} on ${stats.products.toLocaleString()} products · ${stats.blobs.toLocaleString()} distinct files · ${(stats.bytes / 1024 / 1024).toFixed(1)} MB`
              : '—'}
          </span>
        </div>
        <Banner icon="🖼">
          Walks a folder on the server (under the import root) and attaches each photo to its products. Images published
          this way appear on the articles we inject into TecDoc. Always dry-run first — a low match count usually means
          the wrong brand or the wrong matching mode, not an empty folder.
        </Banner>
        <Toolbar>
          <Field label="Folder">
            <input className="input mono" value={dir} onChange={(e) => setDir(e.target.value)} style={{ minWidth: 220 }} />
          </Field>
          <Field label="Brand">
            <input className="input" value={brand} onChange={(e) => setBrand(e.target.value)} style={{ width: 160 }} />
          </Field>
          <Field label="Match by">
            <select
              className="input"
              style={{ width: 'auto' }}
              value={mode}
              onChange={(e) => setMode(e.target.value as ImageImportMode)}
            >
              <option value="sistemcar">Grafico workbook (exploded views → many parts)</option>
              <option value="filename">File name is the code</option>
              <option value="pdf">Inside PDF datasheets (TAB)</option>
            </select>
          </Field>
          <button className="btn btn-sm" disabled={!!busy} onClick={() => void run(true)}>
            Dry run
          </button>
          <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => void run(false)}>
            Import
          </button>
          {busy && <span className="text-[12px] text-muted">{busy}</span>}
        </Toolbar>
        {err && <ErrorBox>{err}</ErrorBox>}
        {res && (
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex flex-wrap gap-4">
              <Stat label="Files found" value={res.files} />
              {res.workbooks > 0 && <Stat label="Grafico workbooks" value={res.workbooks} />}
              {res.boilerplateDropped ? <Stat label="Boilerplate dropped" value={res.boilerplateDropped} /> : null}
              <Stat label="Matched a product" value={res.matched} />
              <Stat label={res.dryRun ? 'Would attach' : 'Attached'} value={res.dryRun ? res.links : res.added} />
              <Stat label="No product" value={res.unmatchedCount} />
              {res.unknownCodes > 0 && <Stat label="Codes not in catalog" value={res.unknownCodes} />}
            </div>
            {res.unmatched.length > 0 && (
              <div className="text-[11.5px] text-faint">
                <b>Unmatched (first {res.unmatched.length}):</b> <span className="mono">{res.unmatched.join(', ')}</span>
              </div>
            )}
            {res.errors.length > 0 && (
              <ErrorBox>
                {res.errors.map((e) => `${e.file}: ${e.error}`).join(' · ')}
              </ErrorBox>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImportProducts() {
  const { request, download } = useApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [parse, setParse] = useState<ImportParse | null>(null);
  const [batches, setBatches] = useState<ImportBatch[] | null>(null);
  const [commit, setCommit] = useState<CommitSummary | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Whether this import's name and description replace what is already on the
   * product.
   *
   * Off by default, and the default is the careful one: a catalogue that named a
   * part usually knows it better than a spreadsheet, so an import fills a gap
   * rather than overwriting. But that makes a file whose whole purpose IS the
   * copy — 25 WD-40 descriptions, say — silently do nothing to a product that
   * already has one, and there was no way to say otherwise from here.
   */
  const [overwrite, setOverwrite] = useState(false);

  const loadBatches = useCallback(async () => {
    const r = await request<{ items: ImportBatch[] }>('GET', '/v1/admin/import/batches');
    if (r.data?.items) setBatches(r.data.items);
  }, [request]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  async function upload(dry: boolean) {
    setErr('');
    setParse(null);
    setCommit(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr('Choose a CSV or XLSX file first.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(dry ? 'Reading…' : 'Staging…');
    const r = await request<ImportParse | ApiError>('POST', `/v1/admin/import/products${dry ? '?dryRun=true' : ''}`, fd);
    setBusy('');
    const d = r.data;
    if (d && 'products' in d) {
      setParse(d);
      if (!dry) void loadBatches();
    } else setErr(d?.error ?? `Import failed (HTTP ${r.status}).`);
  }

  async function builtin(dataset: 'tab' | 'sistemcar') {
    setErr('');
    setParse(null);
    setCommit(null);
    setBusy(`Reading the ${dataset} files on the server…`);
    const r = await request<ImportParse | ApiError>('POST', '/v1/admin/import/builtin', { dataset });
    setBusy('');
    const d = r.data;
    if (d && 'products' in d) {
      setParse(d);
      void loadBatches();
    } else setErr(d?.error ?? `Import failed (HTTP ${r.status}).`);
  }

  /**
   * Commit in slices. A full catalog is ~19k products — minutes of work — which no
   * single request should hold open, so the server commits a slice and reports what
   * is left; we loop until it says zero. `commitStaged` is resumable, so a closed
   * tab costs nothing but the remainder.
   */
  async function runCommit(batch: string, pending: number, replace = overwrite) {
    setErr('');
    setCommit(null);
    setProgress({ done: 0, total: pending });
    let done = 0;
    const acc: CommitSummary = { processed: 0, committed: 0, failed: 0, oes: 0, cross: 0, remaining: 0, errors: [] };

    for (;;) {
      setBusy(`Committing ${done}/${pending}…`);
      const r = await request<CommitSummary | ApiError>('POST', '/v1/admin/import/commit', {
        batch,
        limit: SLICE,
        ...(replace ? { overwrite: { name: true, description: true } } : {}),
      });
      const d = r.data;
      if (!d || !('committed' in d)) {
        setErr(d?.error ?? `Commit failed (HTTP ${r.status}).`);
        break;
      }
      const s = d;
      acc.processed += s.processed;
      acc.committed += s.committed;
      acc.failed += s.failed;
      acc.oes += s.oes;
      acc.cross += s.cross;
      acc.remaining = s.remaining;
      acc.errors = [...acc.errors, ...s.errors].slice(0, 50);
      done += s.processed;
      setProgress({ done, total: pending });
      if (s.processed === 0 || s.remaining === 0) break;
    }
    setBusy('');
    setProgress(null);
    setCommit(acc);
    void loadBatches();
  }

  /**
   * Undo a batch, in slices like the commit. Re-importing cannot fix a bad import
   * on its own (a cross edge keyed by relation is added beside the old one, and
   * OEs only ever accumulate), so correcting a catalog means reverting first.
   */
  async function runRevert(batch: string, committed: number) {
    const ok = window.confirm(
      `Remove the ${committed} product(s) this import created?\n\n` +
        'Products we invented are deleted. Products a catalog also carries are kept — only our ' +
        'data comes off them. The batch returns to pending.',
    );
    if (!ok) return;
    setErr('');
    setCommit(null);
    setProgress({ done: 0, total: committed });
    let done = 0;
    for (;;) {
      setBusy(`Reverting ${done}/${committed}…`);
      const r = await request<RevertSummary | ApiError>('POST', '/v1/admin/import/revert', { batch, limit: SLICE });
      const d = r.data;
      if (!d || !('processed' in d)) {
        setErr(d?.error ?? `Revert failed (HTTP ${r.status}).`);
        break;
      }
      done += d.processed;
      setProgress({ done, total: committed });
      if (d.processed === 0 || d.remaining === 0) break;
    }
    setBusy('');
    setProgress(null);
    await loadBatches();
  }

  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: '1fr' }}>
      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Import products</h3>
          <span className="text-[11.5px] text-faint">CSV or XLSX → staged → your catalog</span>
        </div>
        <div className="blk-body">
          <Banner icon="↓">
            Fill the template: <b>brand</b> and <b>article</b> are required. <b>oe</b> and <b>cross</b> take several
            values separated by <code>;</code> — a cross is <code>BRAND|ARTICLE</code>. <b>relation</b> defaults to{' '}
            <code>equivalent</code>. Any <code>attr:…</code> column becomes a product attribute.
            <button className="btn btn-sm ml-2" onClick={() => download('/v1/admin/import/template', 'product-import-template.xlsx')}>
              Download template
            </button>
          </Banner>

          <Field label="Product file">
            <input ref={fileRef} className="input" type="file" accept=".csv,.xlsx,.xls" onChange={() => setParse(null)} />
          </Field>

          <Toolbar className="!mb-0 mt-3">
            <button className="btn btn-primary" onClick={() => upload(true)} disabled={!!busy}>
              Preview
            </button>
            <button className="btn" onClick={() => upload(false)} disabled={!!busy}>
              Stage file
            </button>
            <span className="flex-1" />
            <span className="text-[11.5px] text-faint">Supplier sets on the server:</span>
            <button className="btn btn-sm" onClick={() => builtin('tab')} disabled={!!busy}>
              TAB batteries
            </button>
            <button className="btn btn-sm" onClick={() => builtin('sistemcar')} disabled={!!busy}>
              SISTEMCAR parts
            </button>
          </Toolbar>

          {busy && <div className="mt-3"><Loading label={busy} /></div>}
          {err && <div className="mt-3"><ErrorBox>{err}</ErrorBox></div>}
          {parse && <Preview p={parse} />}
        </div>
      </div>

      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Staged imports</h3>
          <span className="text-[11.5px] text-faint">nothing is live until you commit</span>
        </div>
        <div className="blk-body">
          {progress && (
            <div className="mb-3 text-[12px] text-muted">
              Committing {progress.done}/{progress.total} — you can leave; it resumes where it stopped.
            </div>
          )}
          {commit && (
            <div className="mb-3 rounded-md border border-line p-3 text-[12px]">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-ok">{commit.committed} committed</span>
                {commit.failed > 0 && <span className="text-bad">{commit.failed} failed</span>}
                <Stat label="OEs" value={commit.oes} />
                <Stat label="cross edges" value={commit.cross} />
              </div>
              {commit.errors.length > 0 && (
                <div className="mono mt-2 max-h-32 overflow-auto text-[11.5px] text-bad">
                  {commit.errors.map((e, i) => (
                    <div key={i}>
                      {e.code}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="mb-2 flex items-center gap-2 text-[12.5px] text-muted">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={!!busy}
            />
            <span>
              Replace the name and description already on the product
              <span className="text-faint">
                {' '}
                — off, an import only fills what is empty, so a file of new descriptions changes nothing on a
                product that already has one
              </span>
            </span>
          </label>

          {batches === null ? (
            <Loading />
          ) : batches.length === 0 ? (
            <Empty>Nothing staged.</Empty>
          ) : (
            <Table cols={['Batch', 'Source', 'Products', 'Pending', 'Committed', 'Failed', '']}>
              {batches.map((b) => (
                <tr key={b.batch}>
                  <td className="mono text-[11.5px]">{b.batch}</td>
                  <td>{b.dataset}</td>
                  <td>{b.total}</td>
                  <td>{b.pending > 0 ? <b>{b.pending}</b> : <span className="text-faint">0</span>}</td>
                  <td className="text-ok">{b.committed}</td>
                  <td className={b.failed ? 'text-bad' : 'text-faint'}>{b.failed}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={!!busy || b.pending === 0}
                        onClick={() => void runCommit(b.batch, b.pending)}
                      >
                        {b.pending > 0 ? `Commit ${b.pending}` : 'Done'}
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={!!busy || b.committed === 0}
                        title="Remove the products this import created and return the batch to pending"
                        onClick={() => void runRevert(b.batch, b.committed)}
                      >
                        Revert {b.committed > 0 ? b.committed : ''}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </div>

      <ImageImport />
    </div>
  );
}
